import { getTodayDateKey } from 'redux/features/auth/authSlice';
import { createTransform, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

const DRAFT_PREFIX = 'prevencor_draft_';
const DRAFT_MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 horas
const SESSION_MARKER_KEY = 'prevencor_tab_session_active';
const LAST_ACTIVE_DATE_KEY = 'prevencor_last_active_date';

function cleanupStaleDrafts() {
  if (typeof window === 'undefined' || !window.localStorage) return;

  try {
    const now = Date.now();
    const keysToRemove = [];

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(DRAFT_PREFIX)) {
        try {
          const raw = window.localStorage.getItem(key);
          const parsed = raw ? JSON.parse(raw) : null;
          const updatedAt = parsed?.updatedAt ? Date.parse(parsed.updatedAt) : Number.NaN;
          if (Number.isNaN(updatedAt) || now - updatedAt > DRAFT_MAX_AGE_MS) {
            keysToRemove.push(key);
          }
        } catch {
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // ignora falha de acesso ao localStorage
  }
}

export function isValidAuthEntry(entry, role) {
  if (!entry || typeof entry !== 'object') return false;
  if (!entry.user || typeof entry.user !== 'object') return false;
  if (!entry.token || typeof entry.token !== 'string') return false;

  if (role === 'doctor' && entry.user.codMed == null && entry.user.CODMED == null) return false;
  if (role === 'func' && entry.user.codFunc == null && entry.user.CODFUNC == null) return false;
  if (role === 'patient' && entry.user.codPac == null && entry.user.CODPAC == null) return false;

  const now = Date.now();
  if (entry.expiresAt && Number(entry.expiresAt) <= now) {
    return false;
  }

  const today = getTodayDateKey();
  if (entry.loginDate && entry.loginDate !== today) {
    return false;
  }

  return true;
}

const authSanitizeTransform = createTransform(
  // inbound: ao salvar no localStorage, nunca persiste loading = true
  (inboundState) => {
    if (!inboundState || typeof inboundState !== 'object') return inboundState;
    return {
      ...inboundState,
      loading: {
        patient: false,
        doctor: false,
        func: false,
      },
    };
  },
  // outbound: ao reidratar do localStorage, valida tokens, datas e limpa estados travados
  (outboundState) => {
    cleanupStaleDrafts();

    const defaultLoading = {
      patient: false,
      doctor: false,
      func: false,
    };

    if (!outboundState || typeof outboundState !== 'object') {
      return {
        patient: null,
        doctor: null,
        func: null,
        loading: defaultLoading,
      };
    }

    const today = getTodayDateKey();
    let isStaleDayAfterClose = false;

    if (typeof window !== 'undefined') {
      try {
        const lastActiveDate = window.localStorage.getItem(LAST_ACTIVE_DATE_KEY);
        const hasTabSession = window.sessionStorage.getItem(SESSION_MARKER_KEY) === '1';

        if (lastActiveDate && lastActiveDate !== today && !hasTabSession) {
          isStaleDayAfterClose = true;
        }

        window.localStorage.setItem(LAST_ACTIVE_DATE_KEY, today);
        window.sessionStorage.setItem(SESSION_MARKER_KEY, '1');
      } catch {
        // ignora erros de storage bloqueado
      }
    }

    if (isStaleDayAfterClose) {
      return {
        patient: null,
        doctor: null,
        func: null,
        loading: defaultLoading,
      };
    }

    return {
      patient: isValidAuthEntry(outboundState.patient, 'patient') ? outboundState.patient : null,
      doctor: isValidAuthEntry(outboundState.doctor, 'doctor') ? outboundState.doctor : null,
      func: isValidAuthEntry(outboundState.func, 'func') ? outboundState.func : null,
      loading: defaultLoading,
    };
  },
  { whitelist: ['auth'] }
);

export default (reducers) => {
  const persistedReducer = persistReducer(
    {
      key: 'prevencor-localDB',
      storage,
      whitelist: ['auth'],
      transforms: [authSanitizeTransform],
    },
    reducers
  );
  return persistedReducer;
};
